import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { collection, getDocs, query, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { 
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer, 
  TableHead, TableRow, Chip, CircularProgress, Grid, Card, CardContent,
  InputAdornment, TextField, Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Select, MenuItem, Divider, Avatar
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import TrendingDownIcon from '@mui/icons-material/TrendingDown'; 
import TrendingUpIcon from '@mui/icons-material/TrendingUp'; 
import PersonIcon from '@mui/icons-material/Person';
import HistoryIcon from '@mui/icons-material/History';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';

function MusteriBakiyeler() {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const sube = queryParams.get('sube')?.trim();

  const [bakiyeler, setBakiyeler] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  
  // Toplam İstatistikler
  const [toplamAlacak, setToplamAlacak] = useState(0); 
  const [toplamBorc, setToplamBorc] = useState(0);

  // DETAY MODAL STATE'LERİ
  const [openModal, setOpenModal] = useState(false);
  const [selectedMusteri, setSelectedMusteri] = useState(null);
  
  // YENİ İŞLEM / DÜZELTME FORM STATE'LERİ
  const [islemTipi, setIslemTipi] = useState("Tahsilat"); 
  const [nakitTutar, setNakitTutar] = useState("");
  const [havaleTutar, setHavaleTutar] = useState("");
  const [aciklama, setAciklama] = useState("");

  // --- VERİ ÇEKME FONKSİYONU ---
  const fetchData = async () => {
    if (!sube) return;
    setLoading(true);
    try {
      // 1. Müşterileri Çek
      const musterilerSnap = await getDocs(collection(db, 'subeler', sube, 'musteriler'));
      const musteriListesi = musterilerSnap.docs.map(doc => ({
        id: doc.id,
        ad: `${doc.data().isim} ${doc.data().soyisim || ''}`.trim(),
        ...doc.data()
      }));

      // 2. Tüm İşlemleri Çek
      const islemlerSnap = await getDocs(query(collection(db, 'subeler', sube, 'islemler')));
      const islemler = islemlerSnap.docs.map(doc => ({
          ...doc.data(),
          timestampDate: doc.data().timestamp?.toDate ? doc.data().timestamp.toDate() : new Date()
      }));

      // 3. Hesaplama Motoru
      const hesaplananBakiyeler = musteriListesi.map(musteri => {
        const musteriIslemleri = islemler.filter(islem => 
          islem.musteri && islem.musteri.id === musteri.id
        );

        musteriIslemleri.sort((a, b) => b.timestampDate - a.timestampDate);

        let bakiyeTL = 0;

        musteriIslemleri.forEach(islem => {
          const odenen = (islem.nakit || 0) + (islem.havaleEft || 0) + (islem.krediKarti || 0);
          const borc = islem.toplamTutar || 0;
          
          if (islem.islemTuru === 'Satış' || islem.islemTuru === 'Borç Ekle') {
             bakiyeTL += (borc - odenen); 
          }
          else if (islem.islemTuru === 'Alış' || islem.islemTuru === 'Alacak Ekle') {
             bakiyeTL -= (borc - odenen); 
          }
          else if (islem.islemTuru === 'Giriş' || islem.islemTuru === 'Tahsilat') { 
             bakiyeTL -= odenen;
          }
          else if (islem.islemTuru === 'Çıkış' || islem.islemTuru === 'Ödeme') { 
             bakiyeTL += odenen;
          }
        });

        return {
          ...musteri,
          bakiyeTL,
          gecmisIslemler: musteriIslemleri 
        };
      });

      hesaplananBakiyeler.sort((a, b) => Math.abs(b.bakiyeTL) - Math.abs(a.bakiyeTL));

      setBakiyeler(hesaplananBakiyeler);

      const piyasaAlacagi = hesaplananBakiyeler.filter(m => m.bakiyeTL > 0).reduce((acc, curr) => acc + curr.bakiyeTL, 0);
      const piyasaBorcu = hesaplananBakiyeler.filter(m => m.bakiyeTL < 0).reduce((acc, curr) => acc + Math.abs(curr.bakiyeTL), 0);
      
      setToplamAlacak(piyasaAlacagi);
      setToplamBorc(piyasaBorcu);

    } catch (error) {
      console.error("Veri hatası:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [sube]);

  // --- MODAL İŞLEMLERİ ---
  const handleRowClick = (musteri) => {
    setSelectedMusteri(musteri);
    setNakitTutar("");
    setHavaleTutar("");
    setAciklama("");
    setIslemTipi("Tahsilat");
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setSelectedMusteri(null);
  };

  // --- HIZLI İŞLEM KAYDETME ---
  const handleIslemKaydet = async () => {
    const nakit = parseFloat(nakitTutar) || 0;
    const havale = parseFloat(havaleTutar) || 0;
    const toplamOdeme = nakit + havale;

    if (toplamOdeme <= 0) {
        alert("Lütfen en az bir ödeme tutarı giriniz.");
        return;
    }
    if (!sube || !selectedMusteri) return;

    try {
        let kaydedilecekIslem = {
            sube: sube,
            musteri: {
                id: selectedMusteri.id,
                isim: selectedMusteri.isim,
                soyisim: selectedMusteri.soyisim || "",
                telefon: selectedMusteri.telefon || ""
            },
            kategori: "Genel",
            urun: "Bakiye Düzeltme / Finansal İşlem",
            timestamp: serverTimestamp(),
            islemAciklamasi: aciklama || "Hızlı Bakiye İşlemi",
            islemNo: Math.floor(100000 + Math.random() * 900000).toString(),
            nakit: 0,
            havaleEft: 0,
            krediKarti: 0,
            toplamTutar: 0,
        };

        if (islemTipi === "Tahsilat") {
            // Müşteriden Para Alıyoruz
            kaydedilecekIslem.islemTuru = "Satış";
            kaydedilecekIslem.nakit = nakit;
            kaydedilecekIslem.havaleEft = havale;
            kaydedilecekIslem.toplamTutar = 0; 
        } 
        else if (islemTipi === "Ödeme") {
            // Müşteriye Para Veriyoruz
            kaydedilecekIslem.islemTuru = "Alış";
            kaydedilecekIslem.nakit = nakit;
            kaydedilecekIslem.havaleEft = havale;
            kaydedilecekIslem.toplamTutar = 0;
        }
        else if (islemTipi === "Borç Ekle") {
            // Manuel Borçlandır (Bizim Alacağımız Artar)
            kaydedilecekIslem.islemTuru = "Satış";
            kaydedilecekIslem.urun = "Manuel Borç Girişi (Devir)";
            kaydedilecekIslem.toplamTutar = toplamOdeme; 
        }
        else if (islemTipi === "Alacak Ekle") {
             // Manuel Alacaklandır (Bizim Borcumuz Artar)
             kaydedilecekIslem.islemTuru = "Alış";
             kaydedilecekIslem.urun = "Manuel Alacak Girişi (Devir)";
             kaydedilecekIslem.toplamTutar = toplamOdeme;
        }

        await addDoc(collection(db, 'subeler', sube, 'islemler'), kaydedilecekIslem);
        
        alert("İşlem başarıyla kaydedildi.");
        handleCloseModal();
        fetchData(); 

    } catch (error) {
        console.error("Kaydetme hatası:", error);
        alert("Bir hata oluştu.");
    }
  };


  // --- FORMAT VE FİLTRELEME ---
  const filteredData = bakiyeler.filter(item => 
    item.ad.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (item.telefon && item.telefon.includes(searchTerm))
  );

  const formatCurrency = (val) => {
    return val.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' TL';
  };

  const formatDate = (date) => {
    return date ? date.toLocaleDateString('tr-TR') : '-';
  };

  const getDurumMetni = (bakiye) => {
      if (bakiye > 0) return "BİZ ALACAKLIYIZ";
      if (bakiye < 0) return "BİZ BORÇLUYUZ";
      return "HESAP KAPALI";
  };

  if (loading) {
    return <Box sx={{display:'flex', justifyContent:'center', mt:5}}><CircularProgress /></Box>;
  }

  return (
    <Box className="mb-page" sx={{ p: 3, backgroundColor: '#f3f4f6', minHeight: '100vh' }}>
      
      {/* ÜST BİLGİ KARTLARI */}
      <Grid className="mb-kpi-grid" container spacing={3} sx={{ mb: 4 }}>
        <Grid className="mb-kpi-item" item xs={12} md={6}>
          <Card className="mb-kpi-card" sx={{ borderLeft: '6px solid #2e7d32', boxShadow: 3 }}>
            <CardContent sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box>
                <Typography color="textSecondary" variant="subtitle2" fontWeight="bold">TOPLAM ALACAK (BİZ ALACAKLIYIZ)</Typography>
                <Typography variant="h4" color="success.main" fontWeight="bold">{formatCurrency(toplamAlacak)}</Typography>
              </Box>
              <TrendingUpIcon sx={{ fontSize: 50, color: '#2e7d32', opacity: 0.2 }} />
            </CardContent>
          </Card>
        </Grid>
        <Grid className="mb-kpi-item" item xs={12} md={6}>
          <Card className="mb-kpi-card" sx={{ borderLeft: '6px solid #d32f2f', boxShadow: 3 }}>
            <CardContent sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box>
                <Typography color="textSecondary" variant="subtitle2" fontWeight="bold">TOPLAM BORÇ (BİZ BORÇLUYUZ)</Typography>
                <Typography variant="h4" color="error.main" fontWeight="bold">{formatCurrency(toplamBorc)}</Typography>
              </Box>
              <TrendingDownIcon sx={{ fontSize: 50, color: '#d32f2f', opacity: 0.2 }} />
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* ARAMA VE TABLO */}
      <Paper className="mb-card" elevation={3} sx={{ p: 3, borderRadius: 2 }}>
        <Box className="mb-toolbar" sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 2 }}>
            <AccountBalanceWalletIcon sx={{ fontSize: 32, color: '#1e3a8a' }} />
            <Typography variant="h5" fontWeight="bold" color="#1e3a8a">Müşteri Bakiye Listesi</Typography>
            <Box sx={{ flexGrow: 1 }} />
            <TextField
              className="mb-search"
              variant="outlined"
              size="small"
              placeholder="Müşteri Ara..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment>,
              }}
              sx={{ width: 300 }}
            />
        </Box>

        <TableContainer className="mb-table-wrap" sx={{ maxHeight: 600 }}>
          <Table className="mb-table" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#e2e8f0' }}>MÜŞTERİ ADI</TableCell>
                <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#e2e8f0' }}>TELEFON</TableCell>
                <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#e2e8f0', textAlign: 'center' }}>DURUM</TableCell>
                <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#e2e8f0', textAlign: 'right' }}>NET BAKİYE (TL)</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredData.map((row) => {
                const bakiye = row.bakiyeTL;
                const durumRenk = bakiye > 0 ? "error" : (bakiye < 0 ? "success" : "default"); 
                const durumYazi = getDurumMetni(bakiye);
                
                return (
                  <TableRow 
                    className="mb-data-row"
                    key={row.id} 
                    hover 
                    onClick={() => handleRowClick(row)} 
                    sx={{ cursor: 'pointer', transition: '0.2s', '&:hover': { backgroundColor: '#e3f2fd' } }}
                  >
                    <TableCell className="mb-cell-name" sx={{ fontWeight: '500' }}>{row.ad}</TableCell>
                    <TableCell className="mb-cell-phone">{row.telefon || '-'}</TableCell>
                    <TableCell className="mb-cell-durum" align="center">
                        <Chip 
                            label={durumYazi} 
                            color={durumRenk}
                            size="small"
                            variant={bakiye === 0 ? "outlined" : "filled"}
                            sx={{ fontWeight: 'bold', minWidth: 120 }} 
                        />
                    </TableCell>
                    <TableCell className="mb-cell-bakiye" align="right" sx={{ 
                        fontWeight: 'bold', 
                        fontSize: '1.1rem',
                        color: bakiye > 0 ? '#d32f2f' : (bakiye < 0 ? '#2e7d32' : '#757575')
                    }}>
                        {formatCurrency(Math.abs(bakiye))}
                        <Typography variant="caption" display="block" sx={{ fontWeight:'normal', opacity:0.8, fontSize:'0.75rem' }}>
                            {bakiye > 0 ? "(Biz Alacaklıyız)" : (bakiye < 0 ? "(Biz Borçluyuz)" : "")}
                        </Typography>
                    </TableCell>
                  </TableRow>
                );
              })}
              {filteredData.length === 0 && (
                <TableRow>
                    <TableCell colSpan={4} align="center" sx={{ py: 3 }}>
                        Kayıt bulunamadı.
                    </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* --- DETAY VE DÜZELTME MODALI --- */}
      <Dialog open={openModal} onClose={handleCloseModal} maxWidth="md" fullWidth>
        <DialogTitle sx={{ bgcolor: '#1e3a8a', color: 'white', display:'flex', alignItems:'center', gap:2 }}>
            <Avatar sx={{ bgcolor: 'white', color: '#1e3a8a' }}><PersonIcon /></Avatar>
            <Box>
                <Typography variant="h6">{selectedMusteri?.ad}</Typography>
                <Typography variant="caption" sx={{opacity:0.8}}>{selectedMusteri?.telefon}</Typography>
            </Box>
            <Box sx={{ flexGrow: 1 }} />
            <Chip 
                label={getDurumMetni(selectedMusteri?.bakiyeTL)}
                color={selectedMusteri?.bakiyeTL > 0 ? "error" : (selectedMusteri?.bakiyeTL < 0 ? "success" : "default")}
                sx={{ bgcolor: 'white', color: 'black', fontWeight:'bold' }}
            />
        </DialogTitle>
        <DialogContent dividers>
            <Grid container spacing={3}>
                
                {/* SOL TARAF: İŞLEM GEÇMİŞİ */}
                <Grid item xs={12} md={7}>
                    <Typography variant="subtitle2" sx={{ mb: 1, display:'flex', alignItems:'center', gap:1 }}>
                        <HistoryIcon fontSize="small"/> Son Hareketler
                    </Typography>
                    <Paper variant="outlined" sx={{ maxHeight: 300, overflow: 'auto' }}>
                        <Table size="small" stickyHeader>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Tarih</TableCell>
                                    <TableCell>İşlem</TableCell>
                                    <TableCell align="right">Tutar</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {selectedMusteri?.gecmisIslemler?.length > 0 ? (
                                    selectedMusteri.gecmisIslemler.map((islem, index) => {
                                      const odenen = (islem.nakit || 0) + (islem.havaleEft || 0) + (islem.krediKarti || 0);
                                      const gosterilecekTutar = islem.toplamTutar > 0 ? islem.toplamTutar : odenen;
                                      
                                      return (
                                        <TableRow key={index}>
                                            <TableCell sx={{fontSize:'0.8rem'}}>{formatDate(islem.timestampDate)}</TableCell>
                                            <TableCell sx={{fontSize:'0.8rem'}}>
                                                {islem.islemTuru} <br/>
                                                <span style={{color:'#757575', fontSize:'0.7rem'}}>{islem.urun}</span>
                                            </TableCell>
                                            <TableCell align="right" sx={{fontSize:'0.8rem', fontWeight:'bold'}}>
                                                {formatCurrency(gosterilecekTutar)}
                                            </TableCell>
                                        </TableRow>
                                      );
                                    })
                                ) : (
                                    <TableRow>
                                        <TableCell colSpan={3} align="center">İşlem yok.</TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </Paper>
                </Grid>

                {/* SAĞ TARAF: HIZLI DÜZELTME / TAHSİLAT */}
                <Grid item xs={12} md={5}>
                    <Paper elevation={0} sx={{ p: 2, bgcolor: '#e3f2fd', borderRadius: 2, height:'100%' }}>
                        <Typography variant="subtitle1" fontWeight="bold" color="#1565c0" sx={{ mb: 2, display:'flex', alignItems:'center', gap:1 }}>
                            <AddCircleIcon /> Hızlı İşlem Ekle
                        </Typography>
                        
                        <Typography variant="caption" display="block" sx={{mb:1}}>İşlem Tipi</Typography>
                        <Select
                            value={islemTipi}
                            onChange={(e) => setIslemTipi(e.target.value)}
                            fullWidth
                            size="small"
                            sx={{ mb: 2, bgcolor:'white' }}
                        >
                            <MenuItem value="Tahsilat">Tahsilat Yap (Para Al)</MenuItem>
                            <MenuItem value="Ödeme">Ödeme Yap (Para Ver)</MenuItem>
                            <Divider />
                            {/* DÜZELTİLEN KISIM: İSİMLER DEĞİŞTİ */}
                            <MenuItem value="Borç Ekle">Manuel Borçlandır (Bizim Alacağımız Artar)</MenuItem>
                            <MenuItem value="Alacak Ekle">Manuel Alacaklandır (Bizim Borcumuz Artar)</MenuItem>
                        </Select>

                        <Typography variant="caption" display="block" sx={{mb:1}}>Nakit Tutar (TL)</Typography>
                        <TextField 
                            type="number" 
                            fullWidth 
                            size="small" 
                            value={nakitTutar} 
                            onChange={(e) => setNakitTutar(e.target.value)}
                            sx={{ mb: 2, bgcolor:'white' }}
                            placeholder="0.00"
                        />

                        <Typography variant="caption" display="block" sx={{mb:1}}>Havale / EFT Tutar (TL)</Typography>
                        <TextField 
                            type="number" 
                            fullWidth 
                            size="small" 
                            value={havaleTutar} 
                            onChange={(e) => setHavaleTutar(e.target.value)}
                            sx={{ mb: 2, bgcolor:'white' }}
                            placeholder="0.00"
                        />

                        <Typography variant="caption" display="block" sx={{mb:1}}>Açıklama</Typography>
                        <TextField 
                            fullWidth 
                            size="small" 
                            value={aciklama} 
                            onChange={(e) => setAciklama(e.target.value)}
                            sx={{ mb: 3, bgcolor:'white' }}
                            placeholder="Örn: Elden ödeme, Eski bakiye..."
                        />

                        <Button 
                            variant="contained" 
                            color="primary" 
                            fullWidth 
                            startIcon={<SaveIcon />}
                            onClick={handleIslemKaydet}
                        >
                            KAYDET
                        </Button>
                    </Paper>
                </Grid>

            </Grid>
        </DialogContent>
        <DialogActions>
            <Button onClick={handleCloseModal} startIcon={<CloseIcon />}>Kapat</Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
}

export default MusteriBakiyeler;